import { useAuthStoreShallow } from "@/features/authentication";
import { useReorderFoldersMutation } from "@/shared/services";
import type { IStoriesFolder } from "@/shared/types";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@lib/cn";
import { useStoriesStoreShallow } from "@pages/stories/store";
import { IconFolder, IconGripVertical } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetFolders } from "../hooks/use-get-folders";
import { FolderActions } from "./folder-actions";

export const FoldersList = () => {
  const { folders } = useGetFolders();
  const { mutate: reorderFolders } = useReorderFoldersMutation();
  const isUserOrCreatorLite = useAuthStoreShallow((s) => s.isUserOrCreatorLite);
  const { setFolderId, folderId } = useStoriesStoreShallow((s) => ({
    setFolderId: s.setFolderId,
    folderId: s.folderId,
  }));

  // Local copy of folders for optimistic D&D updates
  const [localFolders, setLocalFolders] = useState<IStoriesFolder[]>([]);

  // Sync local folders with the source folders
  useEffect(() => {
    setLocalFolders(folders);
  }, [folders]);

  const { pathname } = useLocation();
  const navigateToFolder = useCallback(
    (folder: IStoriesFolder) => {
      setFolderId(folder._id);
    },
    [setFolderId],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Reordering folders is restricted to administrators and creators, matching
    // the server-side creator gate on PUT /folders/reorder.
    if (isUserOrCreatorLite) {
      toast.info("Only administrators and creators can manage folders.", {
        toastId: "manage-folder-toast",
      });
      return;
    }

    if (over && active.id !== over.id) {
      const oldIndex = localFolders.findIndex((f) => f._id === active.id);
      const newIndex = localFolders.findIndex((f) => f._id === over.id);

      const newOrder = arrayMove(localFolders, oldIndex, newIndex);

      // Optimistically update local state
      setLocalFolders(newOrder);

      // Send to server
      const folderIds = newOrder.map((f) => f._id);
      reorderFolders(folderIds);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={localFolders.map((f) => f._id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="tw-mt-1.5 tw-space-y-1.5">
          {localFolders.map((folder) => (
            <SortableFolder
              key={folder._id}
              folder={folder}
              isActive={
                folder._id === folderId &&
                pathname.includes("/pages/editor/folder")
              }
              onClick={() => navigateToFolder(folder)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

interface SortableFolderProps {
  folder: IStoriesFolder;
  isActive: boolean;
  onClick: () => void;
}

const SortableFolder = ({ folder, isActive, onClick }: SortableFolderProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: folder._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <FolderItem
        folder={folder}
        isActive={isActive}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
        onClick={onClick}
      />
    </div>
  );
};

interface FolderItemProps {
  folder: IStoriesFolder;
  isActive: boolean;
  isDragging?: boolean;
  dragHandleProps?: any;
  onClick: () => void;
}

const FolderItem = ({
  folder,
  isActive,
  isDragging,
  dragHandleProps,
  onClick,
}: FolderItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "tw-group/folder tw-relative tw-flex tw-h-7 tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-pl-2 tw-pr-1 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-100",
        isActive && "tw-bg-blue-100 hover:tw-bg-blue-100",
        isOpen && !isActive && "tw-bg-blue-100",
      )}
      style={{
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <IconFolder className="tw-size-4 tw-shrink-0 tw-text-blue" />
      <span className="tw-mr-1 tw-inline-block tw-truncate tw-whitespace-nowrap tw-text-blue">
        {folder.name}
      </span>
      <FolderActions
        isActive={isActive}
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        folder={folder}
      />
      <span
        {...dragHandleProps}
        className="tw-group/icon tw-absolute tw-left-0 tw-top-1/2 tw-shrink-0 -tw-translate-x-full -tw-translate-y-1/2 tw-cursor-grab tw-opacity-0 tw-transition-opacity active:tw-cursor-grabbing group-hover/folder:tw-opacity-100"
      >
        <IconGripVertical className="tw-size-4 tw-text-blue-500 group-hover/icon:tw-text-blue" />
      </span>
    </button>
  );
};
